// src/BinaryTunnel.jsx
// Fly-through tunnel for the blog page. The texture is generated procedurally
// into a <canvas> — pale blue wall with darker blue 1s and 0s — so there's no
// asset to ship. Swap makeBinaryTile() for a loaded texture if you want art.
//
// The camera actually travels the length of the barrel and comes out the far
// end, at which point onArrive() fires and the caller swaps in the next scene.
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";

/* -----------------------------------------------------------------------------
 * Procedural texture
 * -------------------------------------------------------------------------- */

const TILE = 1024; // px, square so it tiles cleanly

/* Digit size in world space is the cell size times FONT_SCALE, and FONT_SCALE
 * is pinned near its ceiling (see below), so the grid is the lever on how big
 * the numbers read: fewer cells over the same wall means bigger ones.
 *
 * Coarsening alone would make the tile's own repeat obvious, so TILES_AROUND
 * drops in step — each tile covers more of the barrel, keeping the cell count
 * per tile high while the cells themselves grow. The repeat maths in
 * useTunnelTexture re-derives from these, and this pairing lands cells square
 * in world space to within a fraction of a percent. */
const COLS = 12;
const ROWS = 14;

const WALL_COLOR = "#e5f0ff";
const DIGIT_COLOR = "#0f6bce";

// Fraction of grid cells that hold a digit. At 1 the wall is solid numbers —
// this is the ceiling, since a cell holds one glyph and glyphs can't spill
// into their neighbours. More digits past this means a finer grid, which costs
// digit size.
const FILL_CHANCE = 1;

/* Digits are scattered inside their cell rather than centred in it — centring
 * is what lines them up into columns. The grid stays as bookkeeping only, so
 * the flicker can still repaint one cell at a time.
 *
 * Everything has to stay inside its own cell: a glyph hanging over the edge
 * would get clipped the moment the neighbouring cell repainted. That caps how
 * far a digit can wander, so the base size is kept well under the cell to
 * leave room to move.
 *
 * FONT_SCALE is at its ceiling here: at the top of SIZE_JITTER a glyph already
 * reaches 0.49 of the cell against a 0.5 limit. Raising it would push the big
 * ones over the edge — grow the cells instead. */
const FONT_SCALE = 0.6; // of cell height
const SIZE_JITTER = 0.22; // ± fraction of base size
const TILT = 0.14; // ± radians
const MIN_ALPHA = 0.45;

/* The digits lie along the barrel rather than across it, so a glyph's "up"
 * runs down the tunnel — toward the exit with this on, back toward the mouth
 * with it off. Purely which way round the numbers read as you fly. */
const FLIP_DIGITS = true;

// Conservative glyph half-extents, as fractions of font size.
const GLYPH_HALF_W = 0.42;
const GLYPH_HALF_H = 0.62;

const spread = () => Math.random() * 2 - 1;

/**
 * Builds the tile and returns a redraw hook so single cells can be re-rolled
 * for a flicker effect without regenerating the whole image.
 */
function makeBinaryTile() {
  const canvas = document.createElement("canvas");
  canvas.width = TILE;
  canvas.height = TILE;

  const ctx = canvas.getContext("2d");
  const cellW = TILE / COLS;
  const cellH = TILE / ROWS;

  ctx.fillStyle = WALL_COLOR;
  ctx.fillRect(0, 0, TILE, TILE);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const drawCell = (col, row) => {
    const x = col * cellW;
    const y = row * cellH;

    ctx.fillStyle = WALL_COLOR;
    ctx.fillRect(x, y, cellW, cellH);

    if (Math.random() > FILL_CHANCE) return;

    const size = cellH * FONT_SCALE * (1 + spread() * SIZE_JITTER);
    const tilt = spread() * TILT;

    // How far the tilted glyph reaches from its own centre, which is what's
    // left over for it to wander within the cell.
    const halfW = size * GLYPH_HALF_W;
    const halfH = size * GLYPH_HALF_H;
    const sin = Math.abs(Math.sin(tilt));
    const cos = Math.abs(Math.cos(tilt));
    const slackX = Math.max(0, cellW / 2 - (halfW * cos + halfH * sin));
    const slackY = Math.max(0, cellH / 2 - (halfW * sin + halfH * cos));

    ctx.save();
    ctx.translate(
      x + cellW / 2 + spread() * slackX,
      y + cellH / 2 + spread() * slackY
    );
    ctx.rotate(tilt);
    if (FLIP_DIGITS) ctx.scale(1, -1);
    ctx.globalAlpha = MIN_ALPHA + Math.random() * (1 - MIN_ALPHA);
    ctx.fillStyle = DIGIT_COLOR;
    ctx.font = `bold ${size.toFixed(2)}px "Courier New", monospace`;
    ctx.fillText(Math.random() < 0.5 ? "0" : "1", 0, 0);
    ctx.restore();
  };

  for (let c = 0; c < COLS; c += 1) {
    for (let r = 0; r < ROWS; r += 1) drawCell(c, r);
  }

  return { canvas, drawCell };
}

/* -----------------------------------------------------------------------------
 * Tunnel
 * -------------------------------------------------------------------------- */

const RADIUS = 1.45; // barrel half-width — smaller reads as a tighter shaft
const LENGTH = 32;
const TILES_AROUND = 2; // texture repeats around the barrel; see COLS/ROWS

// Camera flight, running the length of the barrel toward the open far end.
const START_Z = LENGTH / 2 - 2;
const END_Z = -LENGTH / 2 + 1;
const TRAVEL_SECONDS = 4.2;
const REDUCED_MOTION_SECONDS = 1.2;

// Where the wall itself starts washing out to white, in world Z. Fixed in
// space rather than measured from the camera, so the faded end genuinely
// approaches as you fly instead of hanging at a constant distance ahead.
const FADE_START_Z = 4;
const FADE_END_Z = -LENGTH / 2;

// Screen-level white-out, as a fraction of eased travel. It finishes early so
// the page is solidly white for the last stretch and the scene swap lands
// under cover.
const WHITEOUT_START = 0.62;
const WHITEOUT_END = 0.94;

// Extra scroll on top of the camera move, so the walls read faster than the
// travel alone. Negative pulls the pattern toward the camera.
const TRAVEL_DIRECTION = -1;
const SCROLL_SPEED = 0.5;

const FLICKER_INTERVAL = 0.05; // seconds
// Scaled to the grid: this is ~3% of the cells, so the wall turns over at the
// same rate it did on the finer grid rather than four times faster.
const FLICKER_CELLS = 4; // cells re-rolled per flicker

const DRIFT_AMOUNT = 0.09; // sway, kept well inside RADIUS

/** Tile + texture live in a ref: both are mutated every frame. */
function useTunnelTexture() {
  const ref = useRef(null);

  if (ref.current === null) {
    const tile = makeBinaryTile();

    const texture = new THREE.CanvasTexture(tile.canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    // The lengthwise repeat is derived so cells land roughly square in world
    // space — otherwise the digits smear into ovals. A tile spans
    // (2πr / TILES_AROUND) around with COLS columns; match that cell width.
    const cellWidth = (2 * Math.PI * RADIUS) / TILES_AROUND / COLS;
    const repeatY = Math.round(LENGTH / (ROWS * cellWidth));
    texture.repeat.set(TILES_AROUND, repeatY);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;

    ref.current = { tile, texture };
  }

  useEffect(() => {
    const { texture } = ref.current;
    return () => texture.dispose();
  }, []);

  return ref;
}

/**
 * Mixes the wall toward white as it nears the exit. The fade has to be driven
 * by world position, not camera distance — fog would sit at a fixed remove
 * ahead of the camera and never arrive.
 */
function applyExitFade(material) {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uFadeStartZ = { value: FADE_START_Z };
    shader.uniforms.uFadeEndZ = { value: FADE_END_Z };

    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying float vTunnelZ;")
      .replace(
        "#include <project_vertex>",
        `#include <project_vertex>
         vTunnelZ = (modelMatrix * vec4(transformed, 1.0)).z;`
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        `#include <common>
         varying float vTunnelZ;
         uniform float uFadeStartZ;
         uniform float uFadeEndZ;`
      )
      .replace(
        "#include <map_fragment>",
        `#include <map_fragment>
         float exitFade = 1.0 - smoothstep(uFadeEndZ, uFadeStartZ, vTunnelZ);
         diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0), exitFade);`
      );
  };

  material.needsUpdate = true;
}

function Tunnel({ onArrive, whiteoutRef }) {
  const tunnelRef = useTunnelTexture();

  const materialRef = useRef(null);
  const flickerRef = useRef(0);
  const progressRef = useRef(0);
  const arrivedRef = useRef(false);

  // Read through a ref so a re-rendered parent can't strand the frame loop on
  // a stale callback.
  const onArriveRef = useRef(onArrive);
  useEffect(() => {
    onArriveRef.current = onArrive;
  }, [onArrive]);

  const durationRef = useRef(TRAVEL_SECONDS);
  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    durationRef.current = reduced ? REDUCED_MOTION_SECONDS : TRAVEL_SECONDS;
  }, []);

  // Bind the texture after mount rather than through JSX, so the mutable
  // tunnel state is never read during render.
  useEffect(() => {
    const material = materialRef.current;
    if (!material) return;
    material.map = tunnelRef.current.texture;
    applyExitFade(material);
  }, [tunnelRef]);

  useFrame((state, dt) => {
    const { tile: tileNow, texture: textureNow } = tunnelRef.current;

    // Clamp dt so a backgrounded tab doesn't jump the tunnel on return.
    const step = Math.min(dt, 0.1);

    const p = Math.min(1, progressRef.current + step / durationRef.current);
    progressRef.current = p;
    const eased = p * p * (3 - 2 * p); // smoothstep: ease in and out of the run

    textureNow.offset.y += TRAVEL_DIRECTION * SCROLL_SPEED * step;

    // Re-roll a handful of digits so the walls read as live data.
    flickerRef.current += step;
    if (flickerRef.current >= FLICKER_INTERVAL) {
      flickerRef.current = 0;
      for (let i = 0; i < FLICKER_CELLS; i += 1) {
        tileNow.drawCell((Math.random() * COLS) | 0, (Math.random() * ROWS) | 0);
      }
      textureNow.needsUpdate = true;
    }

    // Travel, plus a gentle sway so the shot never feels locked off.
    const t = state.clock.elapsedTime;
    const cam = state.camera;
    cam.position.x = Math.sin(t * 0.4) * DRIFT_AMOUNT;
    cam.position.y = Math.cos(t * 0.33) * DRIFT_AMOUNT;
    cam.position.z = THREE.MathUtils.lerp(START_Z, END_Z, eased);
    cam.rotation.z = Math.sin(t * 0.27) * 0.05;

    // Blow the screen out to white with travel left to spare.
    const whiteout = whiteoutRef.current;
    if (whiteout) {
      const amount = THREE.MathUtils.smoothstep(eased, WHITEOUT_START, WHITEOUT_END);
      whiteout.style.opacity = String(amount);
    }

    if (p >= 1 && !arrivedRef.current) {
      arrivedRef.current = true;
      onArriveRef.current?.();
    }
  });

  return (
    <mesh rotation={[Math.PI / 2, 0, 0]}>
      {/* open-ended so we can sit inside it; BackSide renders the inner wall */}
      <cylinderGeometry args={[RADIUS, RADIUS, LENGTH, 48, 1, true]} />
      <meshBasicMaterial ref={materialRef} side={THREE.BackSide} toneMapped={false} />
    </mesh>
  );
}

/* -----------------------------------------------------------------------------
 * Export
 * -------------------------------------------------------------------------- */

export default function BinaryTunnel({ onArrive }) {
  const whiteoutRef = useRef(null);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {/* Transparent canvas: the barrel is open at the far end, so the page's
          white shows through the opening as the light you're heading for. */}
      <Canvas
        dpr={[1, 1.5]}
        gl={{ antialias: false, alpha: true }}
        camera={{ fov: 78, near: 0.1, far: 100, position: [0, 0, START_Z] }}
      >
        <Tunnel onArrive={onArrive} whiteoutRef={whiteoutRef} />
      </Canvas>

      <div
        ref={whiteoutRef}
        style={{
          position: "absolute",
          inset: 0,
          background: "#fff",
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
